import { useEffect, useRef, useState } from 'react'
import { AdManagementService } from '../../client'
import type { TargetingLocation } from '../../lib/adPromotion'

const MIN_QUERY = 2
const SEARCH_DELAY = 300

type Props = {
  brandId: number
  locations: TargetingLocation[]
  onChange: (locations: TargetingLocation[]) => void
}

export default function LocationPicker({ brandId, locations, onChange }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<TargetingLocation[]>([])
  const [searching, setSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => clearTimeout(debounce.current ?? undefined), [])

  function search(next: string) {
    setQuery(next)
    clearTimeout(debounce.current ?? undefined)

    if (next.length < MIN_QUERY) {
      setResults([])
      return
    }

    setSearching(true)
    debounce.current = setTimeout(async () => {
      try {
        const found = await AdManagementService.searchLocations(next, brandId)
        setResults(found as TargetingLocation[])
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, SEARCH_DELAY)
  }

  function add(location: TargetingLocation) {
    if (!locations.some(({ key }) => key === location.key)) onChange([...locations, location])
    setQuery('')
    setResults([])
  }

  return (
    <div className="relative">
      <div
        onClick={() => inputRef.current?.focus()}
        className="flex min-h-[38px] cursor-text flex-wrap items-center gap-1 rounded-lg border border-gray-300 px-2 py-1.5 focus-within:ring-2 focus-within:ring-indigo-500"
      >
        {locations.map(({ key, name, type }) => (
          <span
            key={key}
            className="inline-flex items-center gap-0.5 rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700"
          >
            {name}
            <span className="ml-0.5 text-[10px] text-indigo-400">({type})</span>
            <button
              type="button"
              aria-label={`Remove ${name}`}
              onClick={(event) => {
                event.stopPropagation()
                onChange(locations.filter((location) => location.key !== key))
              }}
              className="ml-0.5 leading-none text-indigo-400 hover:text-indigo-600"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder="Search..."
          onChange={(event) => search(event.target.value)}
          onBlur={() => setTimeout(() => setResults([]), 150)}
          className="min-w-20 flex-1 bg-transparent py-0.5 text-sm focus:outline-none"
        />
      </div>

      {(searching || results.length > 0) && (
        <div className="absolute z-10 mt-1 max-h-44 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white text-sm shadow-lg">
          {searching ? (
            <div className="flex items-center justify-center gap-2 px-3 py-3 text-gray-400">
              <span className="size-4 shrink-0 animate-spin rounded-full border-2 border-gray-200 border-t-gray-400" />
              <span className="text-xs">Searching...</span>
            </div>
          ) : (
            results.map((location) => (
              <button
                key={location.key}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault()
                  add(location)
                }}
                className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
              >
                <span>{location.name}</span>
                <span className="text-xs text-gray-400">
                  {location.type} · {location.country_code}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
